import { useNearestDesignationsByCategoryQuery } from "react-migration/domains/constraints/designation/hooks/useNearestDesignationQuery";
import { useSelectionSidebarLoading } from "react-migration/layouts/map/Multilayer/Bundle/useSelectionSidebarLoadingState";
import { LayerTypeLayerSummaryProps, SupportedRegions } from "../../../../types";
import { SectionWithHeaderTooltip } from "../common/SectionWithHeaderTooltip";
import { useClosestAmenitiesKeyIndicator } from "./useClosestAmenitiesKeyIndicator";
import { RoundedCard, RoundedCardHeader } from "./RoundedCard";
import { AmenityDistanceInsight } from "./AmenityDistanceInsight";
import * as constants from "./constants";

type AmenitiesLayerSummaryUKProps = LayerTypeLayerSummaryProps;

export function AmenitiesLayerSummaryUK({ selection, onLoadingChange }: Readonly<AmenitiesLayerSummaryUKProps>) {
  const geometry = selection?.feature?.geometry;
  const { distanceLookup, loading, error } = useNearestDesignationsByCategoryQuery(
    geometry,
    constants.CLOSEST_AMENITY_RADIUSES
  );

  useSelectionSidebarLoading(onLoadingChange, loading);

  useClosestAmenitiesKeyIndicator(
    distanceLookup,
    constants.CLOSEST_AMENITY_RADIUSES.length,
    constants.MAX_AMENITY_RADIUS_METERS
  );

  if (error) {
    throw error;
  }

  return (
    <div className="flex flex-col gap-2">
      <SectionWithHeaderTooltip title="Educational institutions" tooltipText="Distance measured from site boundary">
        <RoundedCard>
          <div className="flex flex-col gap-1">
            <RoundedCardHeader>STATE</RoundedCardHeader>
            <div className="flex flex-col gap-2">
              <AmenityDistanceInsight
                subCategoryId={constants.PRIMARY_SCHOOL_CATEGORY}
                designationDistance={distanceLookup.primary_school}
                loading={loading}
              />
              <AmenityDistanceInsight
                subCategoryId={constants.SECONDARY_SCHOOL_CATEGORY}
                designationDistance={distanceLookup.secondary_school}
                loading={loading}
              />
              <AmenityDistanceInsight
                subCategoryId={constants.FURTHER_EDUCATION_CATEGORY}
                designationDistance={distanceLookup.further_education}
                loading={loading}
              />
            </div>
          </div>
        </RoundedCard>
        <RoundedCard>
          <div className="flex flex-col gap-1">
            <RoundedCardHeader>INDEPENDENT</RoundedCardHeader>
            <div className="flex flex-col gap-2">
              <AmenityDistanceInsight
                subCategoryId={constants.PRIMARY_SCHOOL_INDEPENDENT_CATEGORY}
                designationDistance={distanceLookup.primary_school_independent}
                loading={loading}
              />
              <AmenityDistanceInsight
                subCategoryId={constants.SECONDARY_SCHOOL_INDEPENDENT_CATEGORY}
                designationDistance={distanceLookup.secondary_school_independent}
                loading={loading}
              />
              <AmenityDistanceInsight
                subCategoryId={constants.FURTHER_EDUCATION_INDEPENDENT_CATEGORY}
                designationDistance={distanceLookup.further_education_independent}
                loading={loading}
              />
            </div>
          </div>
        </RoundedCard>
      </SectionWithHeaderTooltip>
      <SectionWithHeaderTooltip title="Stops" tooltipText="Distance measured from site boundary">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <RoundedCard>
            <AmenityDistanceInsight
              subCategoryId={constants.BUS_STOP_CATEGORY}
              designationDistance={distanceLookup.bus_stop}
              loading={loading}
            />
          </RoundedCard>
          <RoundedCard>
            <AmenityDistanceInsight
              subCategoryId={constants.TRAIN_STATION_CATEGORY}
              designationDistance={distanceLookup.train_station}
              loading={loading}
            />
          </RoundedCard>
          <RoundedCard>
            <AmenityDistanceInsight
              subCategoryId={constants.SUBWAY_STATION_CATEGORY}
              designationDistance={distanceLookup.metro_station}
              loading={loading}
            />
          </RoundedCard>
        </div>
      </SectionWithHeaderTooltip>
      <SectionWithHeaderTooltip title="Retail" tooltipText="Distance measured from site boundary">
        <RoundedCard>
          <AmenityDistanceInsight
            subCategoryId={constants.SUPERMARKETS_AND_CONVENIENCE_STORES_CATEGORY}
            designationDistance={distanceLookup.supermarkets_and_convenience_stores}
            loading={loading}
          />
        </RoundedCard>
      </SectionWithHeaderTooltip>
      <SectionWithHeaderTooltip title="Healthcare" tooltipText="Distance measured from site boundary">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <RoundedCard>
            <AmenityDistanceInsight
              subCategoryId={constants.GP_SURGERIES_CATEGORY}
              designationDistance={distanceLookup.gp_surgeries}
              loading={loading}
            />
          </RoundedCard>
          <RoundedCard>
            <AmenityDistanceInsight
              subCategoryId={constants.DENTISTS_CATEGORY}
              designationDistance={distanceLookup.dentists}
              loading={loading}
            />
          </RoundedCard>
          <RoundedCard>
            <AmenityDistanceInsight
              subCategoryId={constants.PHARMACIES_CATEGORY}
              designationDistance={distanceLookup.pharmacies}
              loading={loading}
            />
          </RoundedCard>
          <RoundedCard>
            <AmenityDistanceInsight
              subCategoryId={constants.HOSPITALS_CATEGORY}
              designationDistance={distanceLookup.hospitals}
              loading={loading}
            />
          </RoundedCard>
          <RoundedCard>
            <AmenityDistanceInsight
              subCategoryId={constants.CLINICS_CATEGORY}
              designationDistance={distanceLookup.clinics}
              loading={loading}
            />
          </RoundedCard>
        </div>
      </SectionWithHeaderTooltip>
    </div>
  );
}

AmenitiesLayerSummaryUK.icon = "icon-lt-store-line";
AmenitiesLayerSummaryUK.title = "Nearest amenities";
AmenitiesLayerSummaryUK.supportedRegions = [SupportedRegions.england];
